import { GetSellerCustomersResponse } from '@iwoca/lapi-client/iwocapay';

export type TabKey = 'onboarding' | 'spendingLimit';

/** URL path segment under /pay/customers/. Also the `tab_name` sent to Amplitude. */
export type TabPath = 'onboarding' | 'spending-limit';

/** Values sent to the customers API `view` query param. */
export type CustomersView = 'onboarding' | 'spending_limit';

/** One row of the customers list response. */
export type CustomerRow = GetSellerCustomersResponse['data'][number];

/**
 * Fields a column may declare as sortable (design intent).
 */
export const SORT_KEYS = ['buyer_name', 'earliest_interacted_date'] as const;

export type SortKey = (typeof SORT_KEYS)[number];

/**
 * Whitelisted API `ordering` values: each sort key, ascending or descending
 * (`-` prefix). `defaultSort` must be one of these so a fresh page never sends
 * an unsupported ordering.
 */
export type OrderingParam = SortKey | `-${SortKey}`;

/** A toggle in the Table settings drawer. */
export type TableSettingKey = 'latestPayment' | 'declined';

export interface TableSettingConfig {
  key: TableSettingKey;
  /** Drawer row label. Independent of any column header it reveals. */
  label: string;
}

export interface ColumnConfig {
  /** Stable id. */
  id: string;
  /** Column header label. */
  header: string;
  /** API ordering field this column sorts by. */
  sortKey?: SortKey;
  /** Hidden until this Table settings toggle is on. */
  revealedBy?: TableSettingKey;
  /** Renders an info trigger opening the tab's status explainer. */
  infoModal?: boolean;
  /**
   * Tailwind flex utilities claiming a share of the table width. Columns without
   * one divide whatever is left, equally.
   */
  width?: string;
}

export interface PillConfig {
  /** Value written to the tab's filter param (application_stage/spending_limit_status). */
  value: string;
  label: string;
  /** Icon key resolved to an @iwoca/icons component in the toolbar. */
  icon: 'file' | 'shield' | 'declined' | 'pound' | 'refresh';
  /** Hidden until this Table settings toggle is on. */
  revealedBy?: TableSettingKey;
}

export interface TabConfig {
  /** Stable identity used in code + localStorage keys. */
  key: TabKey;
  /** URL path segment under /pay/customers/. */
  path: TabPath;
  /** Human-readable tab label. */
  label: string;
  /** API `view` param this tab sends. */
  view: CustomersView;
  /** Description rendered when the tab is selected. */
  description: string;
  /** Columns in display order. */
  columns: ColumnConfig[];
  /** Toggle pills (no explicit "All"; none selected = all). */
  pills: PillConfig[];
  /** URL/API filter param the pills write. */
  filterParam: 'application_stage' | 'spending_limit_status';
  /** Ordering applied when the URL has no valid sort. Must be in OrderingParam. */
  defaultSort: OrderingParam;
  /** Toggles offered in the Table settings drawer; empty hides the control. */
  settings: TableSettingConfig[];
}
